// AssetDetailsRedux.js - PRD-43 prototype for the restructured Asset Details page
// Leads with actionable intelligence + quick pivots + progressive disclosure
//
// Layout:
//   Row 1: Header (asset name, type badge, quick actions)
//   Row 2: Metrics cards (scan score, vulns, ports, subdomains, drift)
//   Row 3-7: Collapsible sections (attack surface, remediation, hardening, pentest, compliance)
//   Tab bar: Intelligence / Drift & History / Technical Data

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getAsset,
    getAssetHistory,
    triggerAssetScan,
    getAssetRawDataUrl
} from '../../services/assetService';
import { getEvents } from '../../services/driftService';
import CleanModernLayout from '../../components/layouts/CleanModernLayout';
import MetricsRow from '../../components/prototypes/MetricsRow';
import CollapsibleSection from '../../components/prototypes/CollapsibleSection';
import QuickActions from '../../components/prototypes/QuickActions';
import DriftTimeline from '../../components/DriftTimeline';
import DriftEventsList from '../../components/DriftEventsList';
import DriftFindingsTrend from '../../components/DriftFindingsTrend';
import ScanDetailsTab from '../../components/Assets/ScanDetailsTab';
import './AssetDetailsRedux.css';

// ---------- Helpers ----------

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return iso;
    }
}

function effortBadgeTone(effort) {
    if (!effort) return 'neutral';
    const e = String(effort).toLowerCase();
    if (e.includes('high') || e.includes('hard')) return 'high';
    if (e.includes('medium') || e.includes('moderate')) return 'medium';
    if (e.includes('low') || e.includes('easy')) return 'low';
    return 'neutral';
}

function priorityTone(priority) {
    if (!priority) return 'neutral';
    const p = String(priority).toLowerCase();
    if (p.includes('critical') || p.includes('p0')) return 'critical';
    if (p.includes('high') || p.includes('p1')) return 'high';
    if (p.includes('medium') || p.includes('p2')) return 'medium';
    if (p.includes('low') || p.includes('p3')) return 'low';
    return 'info';
}

// ---------- Section content renderers ----------

function AttackSurfaceContent({ attackSurface }) {
    if (!attackSurface) return null;
    const {
        exposedServices = [],
        criticalTechnologies = [],
        misconfigurations = [],
        informationLeakage = [],
        summary
    } = attackSurface;

    return (
        <div className="adr-attack-surface">
            {summary && <p className="adr-section-lede">{summary}</p>}
            <div className="adr-attack-grid">
                {exposedServices.length > 0 && (
                    <div className="adr-attack-card">
                        <h4>Exposed Services</h4>
                        <ul>
                            {exposedServices.map((s, i) => (
                                <li key={i}>{typeof s === 'string' ? s : s.name || s.service || JSON.stringify(s)}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {criticalTechnologies.length > 0 && (
                    <div className="adr-attack-card">
                        <h4>Critical Technologies</h4>
                        <ul>
                            {criticalTechnologies.map((t, i) => (
                                <li key={i}>{typeof t === 'string' ? t : t.name || t.technology || JSON.stringify(t)}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {misconfigurations.length > 0 && (
                    <div className="adr-attack-card">
                        <h4>Misconfigurations</h4>
                        <ul>
                            {misconfigurations.map((m, i) => (
                                <li key={i}>{typeof m === 'string' ? m : m.description || m.issue || JSON.stringify(m)}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {informationLeakage.length > 0 && (
                    <div className="adr-attack-card">
                        <h4>Information Leakage</h4>
                        <ul>
                            {informationLeakage.map((l, i) => (
                                <li key={i}>{typeof l === 'string' ? l : l.description || l.issue || JSON.stringify(l)}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

function RemediationItem({ rec }) {
    const title = rec.title || rec.vulnerability || rec.finding || 'Unnamed finding';
    const priority = rec.priority || rec.severity || null;
    const attackVector = rec.attackVector || rec.vector || null;
    const remediation = rec.remediation || rec.fix || rec.mitigation || null;
    const effort = rec.effort || rec.complexity || null;
    const timeline = rec.timeline || rec.eta || null;
    const cves = rec.cves || rec.cveIds || [];

    return (
        <div className="adr-remediation-item">
            <div className="adr-remediation-header">
                <div className="adr-remediation-title">{title}</div>
                {priority && (
                    <span className={`adr-priority-tag adr-priority-tag--${priorityTone(priority)}`}>
                        {priority}
                    </span>
                )}
            </div>
            {attackVector && (
                <div className="adr-remediation-field">
                    <span className="adr-remediation-label">Attack Vector:</span> {attackVector}
                </div>
            )}
            {remediation && (
                <div className="adr-remediation-field">
                    <span className="adr-remediation-label">Remediation:</span> {remediation}
                </div>
            )}
            <div className="adr-remediation-meta">
                {effort && (
                    <span className={`adr-meta-tag adr-meta-tag--${effortBadgeTone(effort)}`}>
                        Effort: {effort}
                    </span>
                )}
                {timeline && <span className="adr-meta-tag">Timeline: {timeline}</span>}
                {Array.isArray(cves) && cves.length > 0 && (
                    <span className="adr-meta-tag adr-meta-tag--cve">
                        {cves.slice(0, 4).join(', ')}{cves.length > 4 ? ` +${cves.length - 4}` : ''}
                    </span>
                )}
            </div>
        </div>
    );
}

function RemediationPrioritiesContent({ recommendations }) {
    const list = Array.isArray(recommendations) ? recommendations : [];
    if (list.length === 0) return null;
    return (
        <div className="adr-remediation-list">
            {list.map((rec, i) => (
                <RemediationItem key={i} rec={rec} />
            ))}
        </div>
    );
}

function HardeningRecommendationsContent({ defensive }) {
    if (!defensive) return null;

    // defensive can be an object keyed by category or an array
    let categories;
    if (Array.isArray(defensive)) {
        categories = [{ name: 'Recommendations', items: defensive }];
    } else if (typeof defensive === 'object') {
        categories = Object.entries(defensive).map(([name, items]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1'),
            items: Array.isArray(items) ? items : [items].filter(Boolean)
        }));
    } else {
        return null;
    }

    return (
        <div className="adr-hardening">
            {categories.map(({ name, items }, i) => (
                items.length > 0 && (
                    <div key={i} className="adr-hardening-category">
                        <h4>{name}</h4>
                        <ul>
                            {items.map((item, j) => (
                                <li key={j}>
                                    {typeof item === 'string'
                                        ? item
                                        : (item.recommendation || item.control || item.description || JSON.stringify(item))}
                                </li>
                            ))}
                        </ul>
                    </div>
                )
            ))}
        </div>
    );
}

function PentestRoadmapContent({ roadmap }) {
    if (!roadmap) return null;
    const phases = [
        { key: 'reconnaissance', label: 'Phase 1: Reconnaissance' },
        { key: 'scanning', label: 'Phase 2: Scanning & Enumeration' },
        { key: 'exploitation', label: 'Phase 3: Exploitation' },
        { key: 'postExploitation', label: 'Phase 4: Post-Exploitation' }
    ];

    return (
        <div className="adr-pentest-roadmap">
            {phases.map(({ key, label }) => {
                const phase = roadmap[key] || roadmap[key.replace(/([A-Z])/g, '-$1').toLowerCase()];
                if (!phase) return null;

                const steps = Array.isArray(phase) ? phase : (phase.steps || phase.techniques || [phase]);
                const objective = typeof phase === 'object' && !Array.isArray(phase) ? phase.objective : null;

                return (
                    <div key={key} className="adr-phase">
                        <h4>{label}</h4>
                        {objective && <p className="adr-phase-objective">{objective}</p>}
                        <ul>
                            {steps.map((step, j) => (
                                <li key={j}>
                                    {typeof step === 'string'
                                        ? step
                                        : (step.technique || step.action || step.description || JSON.stringify(step))}
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
}

function ComplianceMappingContent({ compliance }) {
    if (!compliance) return null;

    const frameworks = [
        { key: 'owaspTop10', label: 'OWASP Top 10' },
        { key: 'cisControls', label: 'CIS Controls' },
        { key: 'nistCsf', label: 'NIST CSF' }
    ];

    return (
        <div className="adr-compliance">
            {frameworks.map(({ key, label }) => {
                const mapping = compliance[key] || compliance[key.toLowerCase()];
                if (!mapping) return null;

                const items = Array.isArray(mapping) ? mapping : Object.entries(mapping);

                return (
                    <div key={key} className="adr-compliance-framework">
                        <h4>{label}</h4>
                        {Array.isArray(mapping) ? (
                            <ul>
                                {items.map((item, i) => (
                                    <li key={i}>
                                        {typeof item === 'string'
                                            ? item
                                            : (item.control || item.category || JSON.stringify(item))}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <dl>
                                {items.map(([k, v], i) => (
                                    <div key={i} className="adr-compliance-row">
                                        <dt>{k}</dt>
                                        <dd>{typeof v === 'string' ? v : JSON.stringify(v)}</dd>
                                    </div>
                                ))}
                            </dl>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ---------- Intelligence Tab content ----------

function IntelligenceTab({ enrichment }) {
    if (!enrichment || !enrichment.aiAnalysis) {
        return (
            <div className="adr-empty-state">
                <p>No AI enrichment available yet.</p>
                <p className="adr-empty-subtitle">Enrichment runs automatically after a discovery or vuln scan completes.</p>
            </div>
        );
    }

    const ai = enrichment.aiAnalysis;
    const mitre = ai.mitreAttackMapping || ai.mitreMapping || [];
    const tech = ai.technologyAnalysis;
    const changes = ai.changesSinceLastAnalysis;

    return (
        <div className="adr-tab-content">
            {/* Executive Summary */}
            {ai.executiveSummary && (
                <section className="adr-intel-section">
                    <h3>Executive Summary</h3>
                    <p className="adr-intel-text">{ai.executiveSummary}</p>
                </section>
            )}

            {/* Technology Analysis (PRD-42) */}
            {tech && (
                <section className="adr-intel-section">
                    <h3>Technology Analysis</h3>
                    {tech.techHealthScore != null && (
                        <div className="adr-stat-line">
                            <span className="adr-stat-label">Tech Health Score:</span>
                            <span className="adr-stat-value">{tech.techHealthScore}</span>
                        </div>
                    )}
                    {tech.stackOverview && <p className="adr-intel-text">{tech.stackOverview}</p>}
                    {tech.riskAssessment && (
                        <>
                            <h4>Risk Assessment</h4>
                            <p className="adr-intel-text">{tech.riskAssessment}</p>
                        </>
                    )}
                    {Array.isArray(tech.eolTechnologies) && tech.eolTechnologies.length > 0 && (
                        <>
                            <h4>End-of-Life Technologies</h4>
                            <ul>
                                {tech.eolTechnologies.map((t, i) => (
                                    <li key={i}>
                                        {typeof t === 'string'
                                            ? t
                                            : `${t.name || t.technology || 'Unknown'}${t.version ? ' ' + t.version : ''}${t.notes ? ' — ' + t.notes : ''}`}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </section>
            )}

            {/* Changes Since Last Analysis (PRD-42) */}
            {changes && (
                <section className="adr-intel-section">
                    <h3>Changes Since Last Analysis</h3>
                    {changes.summary && <p className="adr-intel-text">{changes.summary}</p>}
                    {changes.riskTrendNarrative && (
                        <p className="adr-intel-text adr-intel-text--muted">
                            <strong>Risk trend:</strong> {changes.riskTrendNarrative}
                        </p>
                    )}
                    {Array.isArray(changes.newFindings) && changes.newFindings.length > 0 && (
                        <>
                            <h4>New Findings</h4>
                            <ul>
                                {changes.newFindings.map((f, i) => (
                                    <li key={i}>{typeof f === 'string' ? f : (f.title || f.description || JSON.stringify(f))}</li>
                                ))}
                            </ul>
                        </>
                    )}
                    {Array.isArray(changes.resolvedFindings) && changes.resolvedFindings.length > 0 && (
                        <>
                            <h4>Resolved Findings</h4>
                            <ul>
                                {changes.resolvedFindings.map((f, i) => (
                                    <li key={i}>{typeof f === 'string' ? f : (f.title || f.description || JSON.stringify(f))}</li>
                                ))}
                            </ul>
                        </>
                    )}
                </section>
            )}

            {/* MITRE ATT&CK Mapping */}
            {Array.isArray(mitre) && mitre.length > 0 && (
                <section className="adr-intel-section">
                    <h3>MITRE ATT&amp;CK Mapping</h3>
                    <div className="adr-mitre-table-wrap">
                        <table className="adr-mitre-table">
                            <thead>
                                <tr>
                                    <th>Tactic</th>
                                    <th>Technique</th>
                                    <th>ID</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mitre.map((t, i) => (
                                    <tr key={i}>
                                        <td>{t.tactic || '—'}</td>
                                        <td>{t.technique || t.name || '—'}</td>
                                        <td>
                                            {t.techniqueId || t.id ? (
                                                <code>{t.techniqueId || t.id}</code>
                                            ) : '—'}
                                        </td>
                                        <td>{t.description || t.rationale || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Raw AI JSON (debug) */}
            <details className="adr-raw-ai">
                <summary>Raw AI Analysis (debug)</summary>
                <pre>{JSON.stringify(ai, null, 2)}</pre>
            </details>
        </div>
    );
}

// ---------- Drift & History Tab content ----------

function DriftHistoryTab({ assetId, history }) {
    // Normalize history into the flat array shape drift components expect
    const historyEntries = Array.isArray(history)
        ? history
        : (history?.results || history?.snapshots || history?.history || history?.scans || []);

    const trendData = historyEntries.map((r) => ({
        date: r.completedAt || r.timestamp || r.scannedAt,
        critical: r.vulnCritical || r.criticalCount || 0,
        high: r.vulnHigh || r.highCount || 0,
        medium: r.vulnMedium || r.mediumCount || 0,
        low: r.vulnLow || r.lowCount || 0
    }));

    return (
        <div className="adr-tab-content">
            <section className="adr-intel-section">
                <h3>Drift Timeline</h3>
                <DriftTimeline assetId={assetId} history={historyEntries} />
            </section>
            <section className="adr-intel-section">
                <h3>Findings Trend</h3>
                {trendData.length > 0 ? (
                    <DriftFindingsTrend data={trendData} />
                ) : (
                    <p className="adr-intel-text adr-intel-text--muted">Not enough scan history yet.</p>
                )}
            </section>
            <section className="adr-intel-section">
                <h3>Drift Events</h3>
                <DriftEventsList assetId={assetId} compact />
            </section>
        </div>
    );
}

// ---------- Technical Data Tab ----------
// ScanDetailsTab expects { assetId, history } and loads its own scan details

function TechnicalDataTab({ assetId, history }) {
    return (
        <div className="adr-tab-content adr-tab-content--wide">
            <ScanDetailsTab assetId={assetId} history={history} />
        </div>
    );
}

// ---------- Main Component ----------

function AssetDetailsRedux() {
    const { assetId } = useParams();
    const navigate = useNavigate();

    const [asset, setAsset] = useState(null);
    const [enrichment, setEnrichment] = useState(null);
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scanningType, setScanningType] = useState(null);
    const [activeTab, setActiveTab] = useState('intelligence');
    const [driftOpenCount, setDriftOpenCount] = useState(0);
    const [scanToast, setScanToast] = useState(null);

    const loadAsset = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [assetResp, historyResp] = await Promise.all([
                getAsset(assetId),
                getAssetHistory(assetId).catch(() => null)
            ]);
            setAsset(assetResp.asset);
            setEnrichment(assetResp.enrichment || null);
            setHistory(historyResp);
        } catch (err) {
            console.error('Failed to load asset:', err);
            setError(err.message || 'Failed to load asset');
        } finally {
            setLoading(false);
        }
    }, [assetId]);

    useEffect(() => { loadAsset(); }, [loadAsset]);

    // Load drift open event count
    useEffect(() => {
        if (!assetId) return;
        (async () => {
            try {
                const events = await getEvents(assetId);
                const open = (events || []).filter(e => e.status === 'open' || !e.status).length;
                setDriftOpenCount(open);
            } catch (err) {
                console.log('Drift events unavailable:', err.message);
            }
        })();
    }, [assetId]);

    // Handlers
    const handleScan = useCallback(async (scanType) => {
        setScanningType(scanType);
        setScanToast(null);
        try {
            const res = await triggerAssetScan(assetId, scanType);
            setScanToast({ kind: 'success', msg: res?.message || `${scanType} scheduled.` });
            // Refresh after a short delay so next scheduled timestamps are current
            setTimeout(() => loadAsset(), 600);
        } catch (err) {
            setScanToast({ kind: 'error', msg: err.message || 'Failed to schedule scan' });
        } finally {
            setScanningType(null);
        }
    }, [assetId, loadAsset]);

    const handleExport = useCallback(async (format) => {
        if (format === 'json' && asset?.lastResultId) {
            try {
                const url = await getAssetRawDataUrl(assetId, asset.lastResultId);
                if (url) window.open(url, '_blank', 'noopener');
            } catch (err) {
                setScanToast({ kind: 'error', msg: 'Export failed: ' + err.message });
            }
            return;
        }
        // PDF/CSV placeholder - route to existing download path when available
        setScanToast({ kind: 'info', msg: `${format.toUpperCase()} export coming soon` });
    }, [assetId, asset]);

    const handlers = useMemo(() => ({
        onVulnClick: () => navigate(`/inventory?asset=${encodeURIComponent(assetId)}&type=vulnerability`),
        onPortsClick: () => navigate(`/inventory?asset=${encodeURIComponent(assetId)}&type=port`),
        onSubdomainsClick: () => navigate(`/inventory?asset=${encodeURIComponent(assetId)}&type=subdomain`),
        onDriftClick: () => navigate(`/drift-alarms?assetId=${encodeURIComponent(assetId)}`),
        onScoreClick: () => setActiveTab('intelligence')
    }), [assetId, navigate]);

    // Derived data
    const ai = enrichment?.aiAnalysis || {};
    const remediationCount = Array.isArray(ai.prioritizedRecommendations) ? ai.prioritizedRecommendations.length : 0;
    const hardeningCount = ai.defensiveRecommendations
        ? (Array.isArray(ai.defensiveRecommendations)
            ? ai.defensiveRecommendations.length
            : Object.values(ai.defensiveRecommendations).reduce((n, v) => n + (Array.isArray(v) ? v.length : 1), 0))
        : 0;

    if (loading) {
        return (
            <CleanModernLayout activePage="assets">
                <div className="adr-wrapper">
                    <div className="adr-loading">Loading asset…</div>
                </div>
            </CleanModernLayout>
        );
    }

    if (error || !asset) {
        return (
            <CleanModernLayout activePage="assets">
                <div className="adr-wrapper">
                    <div className="adr-error">
                        <h2>Asset unavailable</h2>
                        <p>{error || 'Asset not found.'}</p>
                        <button type="button" className="adr-btn" onClick={() => navigate('/assets')}>
                            Back to Assets
                        </button>
                    </div>
                </div>
            </CleanModernLayout>
        );
    }

    return (
        <CleanModernLayout activePage="assets">
            <div className="adr-wrapper">
                {/* Prototype banner */}
                <div className="adr-prototype-banner">
                    <span className="adr-prototype-banner__chip">Prototype</span>
                    <span>
                        PRD-43 Asset Details Redux — compare with the{' '}
                        <button
                            type="button"
                            className="adr-linklike"
                            onClick={() => navigate(`/assets/${assetId}`)}
                        >
                            current Asset Details
                        </button>
                    </span>
                </div>

                {/* Row 1: Header */}
                <header className="adr-header">
                    <div className="adr-header__identity">
                        <div className="adr-header__icon" aria-hidden="true">
                            {asset.assetType === 'ip' || asset.assetType === 'cidr' ? '▣' : '◉'}
                        </div>
                        <div className="adr-header__text">
                            <h1 className="adr-header__name">{asset.value}</h1>
                            <div className="adr-header__meta">
                                <span className="adr-type-badge">{(asset.assetType || 'asset').toUpperCase()}</span>
                                {asset.lastDiscoveryAt && (
                                    <span className="adr-header__timestamp">
                                        Last discovered: {fmtDate(asset.lastDiscoveryAt)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <QuickActions
                        assetId={assetId}
                        asset={asset}
                        onScan={handleScan}
                        onExport={handleExport}
                        scanInProgress={Boolean(scanningType)}
                    />
                </header>

                {/* Toast */}
                {scanToast && (
                    <div className={`adr-toast adr-toast--${scanToast.kind}`} role="status">
                        {scanToast.msg}
                        <button
                            type="button"
                            className="adr-toast__close"
                            onClick={() => setScanToast(null)}
                            aria-label="Dismiss"
                        >
                            ✕
                        </button>
                    </div>
                )}

                {/* Row 2: Metrics cards */}
                <MetricsRow
                    asset={asset}
                    enrichment={enrichment}
                    driftSummary={{ openCount: driftOpenCount }}
                    handlers={handlers}
                />

                {/* Row 3: Attack Surface */}
                {ai.attackSurface && (
                    <CollapsibleSection
                        title="Attack Surface Analysis"
                        subtitle="Exposed services, critical technologies, misconfigurations, and information leakage"
                        defaultOpen={true}
                    >
                        <AttackSurfaceContent attackSurface={ai.attackSurface} />
                    </CollapsibleSection>
                )}

                {/* Row 4: Remediation Priorities */}
                {remediationCount > 0 && (
                    <CollapsibleSection
                        title="Remediation Priorities"
                        subtitle="Ranked by exploitability and impact — what to fix and how"
                        defaultOpen={true}
                        badge={remediationCount}
                        badgeTone="critical"
                    >
                        <RemediationPrioritiesContent recommendations={ai.prioritizedRecommendations} />
                    </CollapsibleSection>
                )}

                {/* Row 5: Hardening Recommendations */}
                {ai.defensiveRecommendations && (
                    <CollapsibleSection
                        title="Hardening Recommendations"
                        subtitle="Proactive security controls to implement"
                        defaultOpen={true}
                        badge={hardeningCount || undefined}
                        badgeTone="info"
                    >
                        <HardeningRecommendationsContent defensive={ai.defensiveRecommendations} />
                    </CollapsibleSection>
                )}

                {/* Row 6: Pentest Roadmap */}
                {ai.penetrationTestingRoadmap && (
                    <CollapsibleSection
                        title="Pentest Roadmap"
                        subtitle="4-phase offensive testing plan (reference material)"
                        defaultOpen={false}
                    >
                        <PentestRoadmapContent roadmap={ai.penetrationTestingRoadmap} />
                    </CollapsibleSection>
                )}

                {/* Row 7: Compliance Mapping */}
                {ai.complianceMapping && (
                    <CollapsibleSection
                        title="Compliance Mapping"
                        subtitle="OWASP Top 10 · CIS Controls · NIST CSF"
                        defaultOpen={false}
                    >
                        <ComplianceMappingContent compliance={ai.complianceMapping} />
                    </CollapsibleSection>
                )}

                {/* Tab bar */}
                <div className="adr-tabs" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'intelligence'}
                        className={`adr-tab ${activeTab === 'intelligence' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('intelligence')}
                    >
                        Intelligence
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'drift'}
                        className={`adr-tab ${activeTab === 'drift' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('drift')}
                    >
                        Drift &amp; History
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'technical'}
                        className={`adr-tab ${activeTab === 'technical' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('technical')}
                    >
                        Technical Data
                    </button>
                </div>

                <div className="adr-tab-panel" role="tabpanel">
                    {activeTab === 'intelligence' && <IntelligenceTab enrichment={enrichment} />}
                    {activeTab === 'drift' && <DriftHistoryTab assetId={assetId} history={history} />}
                    {activeTab === 'technical' && (
                        <TechnicalDataTab assetId={assetId} history={history} />
                    )}
                </div>
            </div>
        </CleanModernLayout>
    );
}

export default AssetDetailsRedux;
